<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

defineProps({
  heading: {
    type: String,
    default: "The Problem It Solves",
  },
  intro: {
    type: String,
    default: "",
  },
  problemLabel: {
    type: String,
    default: "",
  },
  problems: {
    type: Array as () => string[],
    default: () => [],
  },
  solutionLabel: {
    type: String,
    default: "",
  },
  solutions: {
    type: Array as () => string[],
    default: () => [],
  },
  quote: {
    type: String,
    default: "",
  },
  ctaText: {
    type: String,
    default: "",
  },
  bgImageDark: {
    type: String,
    default: "/images/think-bigger-than-a-logo-dark.svg",
  },
  bgImageLight: {
    type: String,
    default: "/images/think-bigger-than-a-logo-light.svg",
  },
  bgAlt: {
    type: String,
    default: "Decorative graphic",
  },
});

const emit = defineEmits<{
  (e: "cta-clicked"): void;
}>();
</script>

<template>
  <div
    class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px] text-center relative overflow-hidden"
  >
    <NuxtImg
      :src="isLight ? bgImageLight : bgImageDark"
      :alt="bgAlt"
      loading="lazy"
      width="947"
      height="332"
      class="absolute -translate-x-1/2 w-[400px] lg:w-[800px] pointer-events-none max-w-full max-lg:hidden top-[27%] left-[47%]"
    />
    <h2
      class="w-full max-w-full text-[28px] lg:text-[48px] font-semibold leading-normal text-center px-4 font-avant-demi bg-gradient-to-r from-[#12DBFF] to-[#BA55FE] bg-clip-text text-transparent"
    >
      {{ heading }}
    </h2>
    <p
      class="self-stretch text-center mt-4 px-4 text-[16px] lg:text-[18px] lg:opacity-70 max-w-[600px] mx-auto font-avant font-normal leading-[22px] tracking-[0.36px]"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ intro }}
    </p>

    <div
      class="relative flex flex-col lg:flex-row items-start justify-between mt-10 gap-8 lg:gap-0 max-w-[964px] mx-auto px-4"
    >
      <div class="relative z-10 text-left w-full lg:w-[35%]">
        <h3
          class="font-semibold font-avant-demi mb-4 text-[20px] leading-normal"
          :class="isLight ? 'text-[#501D71]' : 'text-white'"
        >
          {{ problemLabel }}
        </h3>
        <ul class="space-y-4">
          <li
            v-for="(item, i) in problems"
            :key="i"
            class="flex items-center gap-2 font-avant opacity-70 text-[15px] font-normal leading-normal tracking-[0.75px]"
            :class="isLight ? 'text-black' : 'text-white'"
          >
            <span
              class="inline-block w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"
            ></span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <div class="flex-shrink-0 flex items-center justify-center lg:w-[40%] max-lg:hidden"></div>

      <div class="relative z-10 text-left w-full lg:w-[35%]">
        <h3
          class="font-semibold font-avant-demi mb-4 text-[20px] leading-normal"
          :class="isLight ? 'text-[#501D71]' : 'text-white'"
        >
          {{ solutionLabel }}
        </h3>
        <ul class="space-y-4">
          <li
            v-for="(item, i) in solutions"
            :key="i"
            class="flex items-center gap-2 font-avant opacity-70 text-[15px] font-normal leading-normal tracking-[0.75px]"
            :class="isLight ? 'text-black' : 'text-white'"
          >
            <span
              class="inline-block w-2 h-2 rounded-full bg-purple-400 flex-shrink-0"
            ></span>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="quote" class="mt-12 max-w-[700px] mx-auto px-4">
      <p
        class="text-center italic font-avant text-[18px] lg:text-[20px] leading-relaxed"
        :class="isLight ? 'text-[#501D71]' : 'text-[#CDA0FE]'"
      >
        "{{ quote }}"
      </p>
    </div>

    <div v-if="ctaText" class="flex justify-center mt-16">
      <WrButton
        @click="$emit('cta-clicked')"
        container-class="h-[48px]"
        append-class="text-base font-normal px-6"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </div>
</template>
